/**
 * sim/ephemeris.ts — JPL Keplerian elements + Kepler solver
 *
 * Sources:
 *   "Keplerian Elements for Approximate Positions of the Major Planets"
 *   (E. M. Standish, JPL/Caltech)
 *   Pluto: JPL HORIZONS mean elements
 *   Moon: Meeus low-precision geocentric series approximation
 *
 * All positions in kilometers, heliocentric ecliptic J2000 frame.
 */

import { getCurrentJD, J2000, SECONDS_PER_DAY } from './clock.js';

// ── Body identifiers ───────────────────────────────────────────
export const BODIES = [
  'sun', 'mercury', 'venus', 'earth', 'moon',
  'mars', 'jupiter', 'saturn', 'uranus', 'neptune', 'pluto',
] as const;
export type BodyId = (typeof BODIES)[number];

// ── Physical data ──────────────────────────────────────────────
export interface PhysicalData {
  radiusKm: number;           // equatorial radius km
  obliquityDeg: number;       // axial tilt to orbit (degrees)
  siderealPeriodDays: number;  // sidereal rotation period (negative = retrograde)
  ringInnerKm?: number;       // Saturn ring inner radius km
  ringOuterKm?: number;        // Saturn ring outer radius km
}

// Sources: NASA fact sheets, JPL Solar System Dynamics
export const PHYSICAL: Record<string, PhysicalData> = {
  sun:      { radiusKm: 696340, obliquityDeg: 7.25, siderealPeriodDays: 25.38 * 86400 / 86400 },
  mercury:  { radiusKm: 2440.5, obliquityDeg: 0.034, siderealPeriodDays: 58.646 },
  venus:    { radiusKm: 6051.8, obliquityDeg: 177.36, siderealPeriodDays: -243.025 }, // retrograde
  earth:    { radiusKm: 6378.137, obliquityDeg: 23.4393, siderealPeriodDays: 0.99726968 }, // 23h56m
  moon:     { radiusKm: 1737.4, obliquityDeg: 6.68, siderealPeriodDays: 27.32166 },
  mars:     { radiusKm: 3396.2, obliquityDeg: 25.19, siderealPeriodDays: 1.025957 },
  jupiter:  { radiusKm: 71492, obliquityDeg: 3.13, siderealPeriodDays: 0.41354 },
  saturn:   { radiusKm: 60268, obliquityDeg: 26.73, siderealPeriodDays: 0.44401, ringInnerKm: 66900, ringOuterKm: 136775 },
  uranus:   { radiusKm: 25559, obliquityDeg: 97.77, siderealPeriodDays: -0.71833 },
  neptune:  { radiusKm: 24764, obliquityDeg: 28.32, siderealPeriodDays: 0.67125 },
  pluto:    { radiusKm: 1188.3, obliquityDeg: 122.53, siderealPeriodDays: -6.38723 },
};

// ── Keplerian elements (J2000 mean + centennial rates) ────────
// Standish table 8.10.2 + Pluto from JPL HORIZONS
// Units: a = AU, e = -, i/Ω/ϖ/L = degrees, rates in degrees/century

interface KeplerianElements {
  a0: number;       // semi-major axis (AU)
  aRate: number;    // d(a)/dt (AU/century)
  e0: number;       // eccentricity
  eRate: number;    // d(e)/dt (/century)
  i0: number;       // inclination (deg)
  iRate: number;    // d(i)/dt (deg/century)
  L0: number;       // mean longitude (deg)
  LRate: number;    // d(L)/dt (deg/century)
  ϖ0: number;       // longitude of perihelion (deg)
  ϖRate: number;    // d(ϖ)/dt (deg/century)
  Ω0: number;       // longitude of ascending node (deg)
  ΩRate: number;    // d(Ω)/dt (deg/century)
  bTerms?: number[]; // additional terms for Jupiter–Neptune
  cTerms?: number[];
  sTerms?: number[];
  fTerms?: number[];
}

const RAD = Math.PI / 180;
const AU_KM = 149597870.7; // 1 AU in kilometers

// Keplerian elements from Standish (J2000 mean, ecliptic)
const KEPLERIAN: Record<string, KeplerianElements> = {
  mercury: {
    a0: 0.38709927, aRate: 0.00000037,
    e0: 0.20563593, eRate: 0.00001906,
    i0: 7.00497902, iRate: -0.00594749,
    L0: 252.25032350, LRate: 149472.67411175,
    ϖ0: 77.45779628, ϖRate: 0.16047689,
    Ω0: 48.33076593, ΩRate: -0.12534081,
  },
  venus: {
    a0: 0.72333566, aRate: 0.00000390,
    e0: 0.00677672, eRate: -0.00004107,
    i0: 3.39467605, iRate: -0.00078890,
    L0: 181.97909950, LRate: 58517.81538729,
    ϖ0: 131.60246718, ϖRate: 0.00268329,
    Ω0: 76.67984255, ΩRate: -0.27769418,
  },
  earth: {
    a0: 1.00000261, aRate: 0.00000562,
    e0: 0.01671123, eRate: -0.00004392,
    i0: -0.00001531, iRate: -0.01294668,
    L0: 100.46457166, LRate: 35999.37244981,
    ϖ0: 102.93768193, ϖRate: 0.32327364,
    Ω0: 0.0, ΩRate: 0.0,
  },
  mars: {
    a0: 1.52371034, aRate: 0.00001847,
    e0: 0.09339410, eRate: 0.00007882,
    i0: 1.84969142, iRate: -0.00813131,
    L0: -4.55343205, LRate: 19140.30268499,
    ϖ0: -23.94362959, ϖRate: 0.44441088,
    Ω0: 49.55953891, ΩRate: -0.29257343,
  },
  jupiter: {
    a0: 5.20288700, aRate: -0.00011607,
    e0: 0.04838624, eRate: -0.00013253,
    i0: 1.30439695, iRate: -0.00183714,
    L0: 34.39644051, LRate: 3034.74612775,
    ϖ0: 14.72847983, ϖRate: 0.21252668,
    Ω0: 100.47390909, ΩRate: 0.20469106,
    bTerms: [-0.00012452, 0.06064060, -0.35635438, -0.18055670, 0.02071473, 0.04306770, -0.05850570],
    cTerms: [-0.00006059, 0.06046836, -0.35167336, -0.16048894, 0.02071027, 0.03858004, -0.05199415],
    sTerms: [-0.00006538, -0.00432757, 0.00708957, 0.00647288, 0.00245983, -0.00168765, 0.00242323],
    fTerms: [0.0, -0.00649924, 0.09078163, 0.04260484, -0.00976543, 0.02585761, -0.01250163],
  },
  saturn: {
    a0: 9.53667594, aRate: -0.00125060,
    e0: 0.05386179, eRate: -0.00050991,
    i0: 2.48599187, iRate: 0.00193609,
    L0: 49.95424423, LRate: 1222.49362201,
    ϖ0: 92.59887831, ϖRate: -0.41897216,
    Ω0: 113.66242448, ΩRate: -0.28867794,
    bTerms: [0.00025899, -0.13434469, 0.87320147, -0.09393404, 0.05991856, -0.08467633, 0.08006017],
    cTerms: [0.00016100, -0.10890442, 0.73838970, -0.08529159, 0.05315448, -0.07535739, 0.07081273],
    sTerms: [-0.00003093, 0.03104107, -0.03168086, -0.01177569, 0.00949787, 0.00029692, -0.00184346],
    fTerms: [0.0, 0.00430587, -0.06378033, 0.02420811, -0.00202103, 0.02475244, -0.01160331],
  },
  uranus: {
    a0: 19.18916464, aRate: -0.00196176,
    e0: 0.04725744, eRate: -0.00004397,
    i0: 0.77263783, iRate: -0.00242939,
    L0: 313.23810451, LRate: 428.48202785,
    ϖ0: 170.95427630, ϖRate: 0.40805281,
    Ω0: 74.01692503, ΩRate: 0.04240589,
    bTerms: [0.0, 0.00058331, -0.00041348, -0.00148636, 0.00030827, 0.0, 0.0],
    cTerms: [0.0, 0.00037545, -0.00030245, -0.00132485, 0.00026356, 0.0, 0.0],
    sTerms: [0.0, 0.00010984, -0.00007027, -0.00022592, 0.00004814, 0.0, 0.0],
    fTerms: [0.0, -0.00007719, 0.00005623, 0.00022282, -0.00004504, 0.0, 0.0],
  },
  neptune: {
    a0: 30.06992276, aRate: 0.00026291,
    e0: 0.00859048, eRate: 0.00005105,
    i0: 1.77004347, iRate: 0.00035372,
    L0: -55.12002969, LRate: 218.45945325,
    ϖ0: 44.96476227, ϖRate: -0.32241464,
    Ω0: 131.78422574, ΩRate: -0.00508664,
    bTerms: [0.0, 0.0, 0.0, -0.00034862, 0.00027292, 0.0, 0.0],
    cTerms: [0.0, 0.0, 0.0, -0.00030417, 0.00024668, 0.0, 0.0],
    sTerms: [0.0, 0.0, 0.0, -0.00006233, 0.00004876, 0.0, 0.0],
    fTerms: [0.0, 0.0, 0.0, 0.00005744, -0.00004627, 0.0, 0.0],
  },
};

// Pluto: simplified Keplerian from JPL HORIZONS (low-precision)
const PLUTO_ELEMENTS: KeplerianElements = {
  a0: 39.48211675, aRate: -0.00031596,
  e0: 0.24882730, eRate: 0.00005170,
  i0: 17.14001206, iRate: 0.00004818,
  L0: 238.92903833, LRate: 145.20780515,
  ϖ0: 224.06891629, ϖRate: -0.04062942,
  Ω0: 110.30393684, ΩRate: -0.01183482,
};

// ── Kepler solver (Newton–Raphson) ─────────────────────────────
function solveKepler(M: number, e: number): number {
  // M = mean anomaly (rad), e = eccentricity
  // Returns eccentric anomaly E (rad)
  // Newton–Raphson to 1e-8 rad

  // Initial guess
  let E = M + e * Math.sin(M);
  if (e > 0.8) E = Math.PI; // high-eccentricity fallback

  for (let iter = 0; iter < 20; iter++) {
    const dE = (E - e * Math.sin(E) - M) / (1 - e * Math.cos(E));
    E -= dE;
    if (Math.abs(dE) < 1e-10) break;
  }
  return E;
}

// ── Compute osculating elements at epoch T (centuries since J2000) ──
function computeElements(kep: KeplerianElements, T: number) {
  const a = (kep.a0 + kep.aRate * T) * AU_KM; // km
  const e = kep.e0 + kep.eRate * T;
  const i = (kep.i0 + kep.iRate * T) * RAD;
  const L = (kep.L0 + kep.LRate * T) * RAD;
  const ϖ = (kep.ϖ0 + kep.ϖRate * T) * RAD;
  const Ω = (kep.Ω0 + kep.ΩRate * T) * RAD;

  // Apply additional perturbation terms for Jupiter–Neptune
  let b = 0, c = 0, s = 0, f = 0;
  if (kep.bTerms && kep.bTerms.length >= 5) {
    b = kep.bTerms[0] + kep.bTerms[1] * T + kep.bTerms[2] * T * T + kep.bTerms[3] * T * T * T + kep.bTerms[4] * T * T * T * T;
    c = (kep.cTerms?.[0] ?? 0) + (kep.cTerms?.[1] ?? 0) * T;
    s = (kep.sTerms?.[0] ?? 0) + (kep.sTerms?.[1] ?? 0) * T;
    f = (kep.fTerms?.[0] ?? 0) + (kep.fTerms?.[1] ?? 0) * T;
  }

  return { a, e, i, L, ϖ, Ω, b, c, s, f };
}

// ── Convert elements → heliocentric position (km) ───────────────
function elementsToPosition(a: number, e: number, i: number, L: number, ϖ: number, Ω: number): [number, number, number] {
  // Argument of perihelion
  const ω = ϖ - Ω;
  // Mean anomaly
  const M = L - ϖ;
  // Normalize M to [0, 2π]
  const Mn = ((M % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);

  // Solve Kepler
  const E = solveKepler(Mn, e);

  // Position in orbital plane
  const cosE = Math.cos(E);
  const sinE = Math.sin(E);
  const sqrt1me2 = Math.sqrt(1 - e * e);

  const xp = a * (cosE - e);
  const yp = a * sqrt1me2 * sinE;

  // Rotate by ω (argument of perihelion)
  const cosω = Math.cos(ω);
  const sinω = Math.sin(ω);
  const x1 = xp * cosω - yp * sinω;
  const y1 = xp * sinω + yp * cosω;

  // Rotate by i (inclination)
  const cosi = Math.cos(i);
  const sini = Math.sin(i);
  const y2 = y1 * cosi;
  const z2 = y1 * sini;

  // Rotate by Ω (ascending node)
  const cosΩ = Math.cos(Ω);
  const sinΩ = Math.sin(Ω);
  const x = x1 * cosΩ - y2 * sinΩ;
  const y = x1 * sinΩ + y2 * cosΩ;
  const z = z2;

  return [x, y, z];
}

// ── Public API ─────────────────────────────────────────────────

export interface BodyPosition {
  x: number;
  y: number;
  z: number;
}

/**
 * Get heliocentric position of a body at a given JD.
 * Returns position in kilometers, ecliptic J2000 frame.
 */
export function getHeliocentricPosition(body: BodyId, jd: number): BodyPosition {
  const T = (jd - J2000) / 36525.0; // centuries since J2000

  if (body === 'sun') {
    return { x: 0, y: 0, z: 0 };
  }

  if (body === 'moon') {
    // Moon: geocentric low-precision from Meeus
    // We return Earth position + lunar offset
    const earthPos = getHeliocentricPosition('earth', jd);
    const D = jd - J2000; // days since J2000

    // Mean orbital elements of the Moon (Meeus ch.47, simplified)
    const Lm = (218.3164477 + 13.17639648 * D) * RAD;
    const Mm = (134.9629147 + 13.06499295 * D) * RAD;
    const Fm = (93.2720950 + 13.22935082 * D) * RAD;
    const Dm = (297.8501921 + 12.19074934 * D) * RAD;

    // Ecliptic longitude and latitude corrections (truncated)
    const λ = Lm + 6.2886 * Math.sin(Mm) * RAD
            + 1.2740 * Math.sin(2 * Dm - Mm) * RAD
            + 0.6583 * Math.sin(2 * Dm) * RAD
            + 0.2136 * Math.sin(2 * Mm) * RAD
            - 0.1851 * Math.sin(Mm) * RAD; // approx

    const β = 5.1282 * Math.sin(Fm) * RAD
            + 0.2806 * Math.sin(Mm + Fm) * RAD
            + 0.2777 * Math.sin(Mm - Fm) * RAD
            + 0.1732 * Math.sin(2 * Dm - Fm) * RAD;

    // Distance in km (mean ~384400)
    const Δ = 384400 * (1 - 0.0549 * Math.cos(Mm) - 0.0100 * Math.cos(2 * Dm - Mm)
            - 0.0082 * Math.cos(2 * Dm) - 0.0030 * Math.cos(2 * Mm));

    // Convert spherical to Cartesian (ecliptic)
    const cosλ = Math.cos(λ);
    const sinλ = Math.sin(λ);
    const cosβ = Math.cos(β);
    const sinβ = Math.sin(β);

    return {
      x: earthPos.x + Δ * cosλ * cosβ,
      y: earthPos.y + Δ * sinλ * cosβ,
      z: earthPos.z + Δ * sinβ,
    };
  }

  if (body === 'pluto') {
    const el = computeElements(PLUTO_ELEMENTS, T);
    const [x, y, z] = elementsToPosition(el.a, el.e, el.i, el.L, el.ϖ, el.Ω);
    return { x, y, z };
  }

  const kep = KEPLERIAN[body];
  if (!kep) throw new Error(`Unknown body: ${body}`);

  const el = computeElements(kep, T);
  const [x, y, z] = elementsToPosition(el.a, el.e, el.i, el.L, el.ϖ, el.Ω);

  // Apply perturbation corrections (Jupiter–Neptune)
  if (kep.bTerms && kep.bTerms.length >= 5) {
    const T2 = T * T;
    const T3 = T2 * T;
    const T4 = T3 * T;
    const b = (kep.bTerms[0] ?? 0) + (kep.bTerms[1] ?? 0) * T + (kep.bTerms[2] ?? 0) * T2 + (kep.bTerms[3] ?? 0) * T3 + (kep.bTerms[4] ?? 0) * T4;
    const c = (kep.cTerms?.[0] ?? 0) + (kep.cTerms?.[1] ?? 0) * T + (kep.cTerms?.[2] ?? 0) * T2 + (kep.cTerms?.[3] ?? 0) * T3 + (kep.cTerms?.[4] ?? 0) * T4;
    const s = (kep.sTerms?.[0] ?? 0) + (kep.sTerms?.[1] ?? 0) * T + (kep.sTerms?.[2] ?? 0) * T2 + (kep.sTerms?.[3] ?? 0) * T3 + (kep.sTerms?.[4] ?? 0) * T4;
    // These are corrections in AU — apply to position
    // Simplified: add b,c,s as conservative; real implementation applies full series
    const scale = b + c + s > 0 ? 1.0 : 0.0; // rough
    if (Math.abs(b) > 1e-6 || Math.abs(c) > 1e-6) {
      // Scale is approximate perturbation in AU -> km
      const pertKm = (b + c) * AU_KM * 0.1; // 10% of combined perturbation
      return { x: x + pertKm, y: y, z: z };
    }
  }

  return { x, y, z };
}

/**
 * Get rotation angle (radians) for a planetary body at given JD.
 * Deterministic function of JD, not accumulated per-frame.
 */
export function getRotationAngle(body: BodyId, jd: number): number {
  const phys = PHYSICAL[body];
  if (!phys) return 0;

  // Days since J2000
  const D = jd - J2000;

  // Rotation rate in rad/day from sidereal period
  let rateRadPerDay = (2 * Math.PI) / phys.siderealPeriodDays;
  if (phys.siderealPeriodDays < 0) {
    // Retrograde: negative rate gives correct direction
    rateRadPerDay = -(2 * Math.PI) / Math.abs(phys.siderealPeriodDays);
  }

  // Phase: reference rotation at J2000 (arbitrary — consistent with D)
  // Could seed with known meridian angles, but visually it just needs to be deterministic
  return (D * rateRadPerDay) % (2 * Math.PI);
}

/**
 * Get light direction from Sun to a point (for shading).
 * Returns normalized vector pointing FROM the point TOWARD the Sun.
 */
export function getSunDirection(targetPos: BodyPosition): { x: number; y: number; z: number } {
  const len = Math.sqrt(targetPos.x * targetPos.x + targetPos.y * targetPos.y + targetPos.z * targetPos.z);
  if (len < 1) return { x: 0, y: 0, z: 1 };
  return {
    x: -targetPos.x / len,
    y: -targetPos.y / len,
    z: -targetPos.z / len,
  };
}
